import { useEffect, useRef } from 'react';
import { KeenIcon } from '@/components';
import CircularProgress from '@mui/material/CircularProgress';
import clsx from 'clsx';
import { useGetHarborByIdQuery } from '@/services/HarborManagementService';
import { IHarborData } from '@/types/harbor-management.types';
import { formatFinnishCoordinate, formatInternationalPhoneNumber } from '@/utils';
import { useManagedListOptions } from '@/hooks/useManagedListOptions';
import { ManagedListType } from '@/types/list-value.types';

export const HarborRequestViewModal = ({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}) => {
  const { data, isLoading } = useGetHarborByIdQuery(id);
  const { getLabel: getHarborCategoryLabel } = useManagedListOptions(
    ManagedListType.HARBOR_CATEGORY
  );
  const { getLabel: getHarborFacilityLabel } = useManagedListOptions(
    ManagedListType.HARBOR_FACILITY
  );

  // Some fields may exist only on create-request payloads but not on HarborResponse
  const extended =
    (data as unknown as Partial<IHarborData> & {
      depth?: number | string;
      business_hours?: unknown;
    }) || {};

  const businessHoursObj: Record<
    string,
    { open?: string; close?: string }
  > | null = (() => {
    const bh = extended.business_hours as unknown;
    if (!bh) return null;
    if (typeof bh === 'object')
      return bh as Record<string, { open?: string; close?: string }>;
    if (typeof bh === 'string') {
      try {
        const parsed = JSON.parse(bh);
        if (parsed && typeof parsed === 'object') {
          return parsed as Record<string, { open?: string; close?: string }>;
        }
      } catch {
        return null;
      }
    }
    return null;
  })();

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isLoading && modalRef.current) {
      const timer = setTimeout(() => {
        modalRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <CircularProgress aria-label="Loading request details" />
        <p className="mt-2">Loading request details...</p>
      </div>
    );
  }

  return (
    <div
      ref={modalRef}
      tabIndex={-1}
      autoFocus
      className="w-full max-w-2xl bg-gray-100 rounded-2xl shadow-xl mx-auto relative max-h-[80vh] flex flex-col"
      aria-labelledby="modal-title"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="modal-close"
        className="absolute top-4 right-4 rounded-md text-black hover:text-gray-900 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 contrast:text-white contrast:hover:text-white contrast:hover:bg-white/20"
        aria-label="Close modal"
      >
        <KeenIcon type="filled" icon="cross" className="h-6 w-6" />
      </button>

      <h1
        id="modal-title"
        className="text-lg font-medium leading-6 text-gray-900 border-b p-4"
      >
        Harbor Details
      </h1>

      <div
        className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 overflow-auto px-6 pb-6 custom-scrollbar"
        tabIndex={0}
        aria-label="Harbor request details"
      >
        <div className="form-control">
          <div className="label">
            <span className="label-text">Name</span>
          </div>
          <div className={clsx('input input-bordered w-full')}>
            {data?.name || '-'}
          </div>
        </div>
        <div className="form-control sm:col-span-2">
          <div className="label">
            <span className="label-text">Description</span>
          </div>
          <div className={clsx('textarea textarea-bordered w-full')}>
            {data?.description || '-'}
          </div>
        </div>
        <div className="form-control">
          <div className="label">
            <span className="label-text">Website</span>
          </div>
          <div className={clsx('input input-bordered w-full')}>
            {data?.website_url || data?.website || '-'}
          </div>
        </div>
        <div className="form-control">
          <div className="label">
            <span className="label-text">Contact Email</span>
          </div>
          <div className={clsx('input input-bordered w-full')}>
            {data?.contact_email || '-'}
          </div>
        </div>
        <div className="form-control">
          <div className="label">
            <span className="label-text">Contact Phone</span>
          </div>
          <div className={clsx('input input-bordered w-full')}>
            {data?.contact_phone
              ? formatInternationalPhoneNumber(
                  data.contact_phone,
                  data.country_code,
                  data.country_short_code
                )
              : '-'}
          </div>
        </div>
        <div className="form-control">
          <div className="label">
            <span className="label-text">Category</span>
          </div>
          <div className={clsx('input input-bordered w-full')}>
            {data?.category
              ? getHarborCategoryLabel(data.category)
              : '-'}
          </div>
        </div>
        <div className="form-control">
          <div className="label">
            <span className="label-text">Berth Capacity</span>
          </div>
          <div className={clsx('input input-bordered w-full')}>
            {data?.berth_capacity || '-'}
          </div>
        </div>
        <div className="form-control">
          <div className="label">
            <span className="label-text">Business Hours</span>
          </div>
          {businessHoursObj ? (
            <div className="border border-gray-300 rounded-lg p-3 bg-light-active max-h-48 overflow-auto">
              <ul className="space-y-1 text-sm">
                {Object.entries(businessHoursObj)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([day, hours]) => (
                    <li key={day} className="flex justify-between">
                      <span className="capitalize text-gray-600">{day}</span>
                      <span className="font-medium text-gray-800">
                        {hours?.open && hours?.close
                          ? `${hours.open} - ${hours.close}`
                          : '-'}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          ) : (
            <div className={clsx('input input-bordered w-full')}>-</div>
          )}
        </div>
        <div className="form-control">
          <div className="label">
            <span className="label-text">VHF Channel</span>
          </div>
          <div className={clsx('input input-bordered w-full')}>
            {data?.vhf_channel || '-'}
          </div>
        </div>
        <div className="form-control">
          <div className="label">
            <span className="label-text">Coordinates</span>
          </div>
          <div className={clsx('input input-bordered w-full')}>
            {data?.latitude && data?.longitude
              ? `${formatFinnishCoordinate(data.latitude)}, ${formatFinnishCoordinate(data.longitude)}`
              : '-'}
          </div>
        </div>
        <div className="form-control">
          <div className="label">
            <span className="label-text">Depths</span>
          </div>
          <div className={clsx('input input-bordered w-full')}>
            {data?.min_depth || data?.max_depth
              ? `${data?.min_depth ?? '-'} - ${data?.max_depth ?? '-'} ${data?.depth_unit ?? ''}`.trim()
              : ((extended?.depth as string | number | undefined) ?? '-')}
          </div>
        </div>
        <div className="form-control sm:col-span-2">
          <div className="label">
            <span className="label-text">Facilities</span>
          </div>
          {Array.isArray(data?.facilities) && data.facilities.length > 0 ? (
            <div className="border rounded-lg p-3 bg-gray-100">
              <ul className="flex flex-wrap gap-2">
                {data.facilities.map((f: string) => (
                  <li key={f} className="badge badge-light capitalize">
                    {getHarborFacilityLabel(f)}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className={clsx('input input-bordered w-full')}>-</div>
          )}
        </div>
        <div className="form-control sm:col-span-2">
          <div className="label">
            <span className="label-text">Services</span>
          </div>
          {Array.isArray(extended?.services) &&
          (extended.services?.length ?? 0) > 0 ? (
            <div className="border rounded-lg p-3 bg-gray-100">
              <ul className="flex flex-wrap gap-2">
                {(
                  extended.services as Array<
                    { id?: string; name?: string } | string
                  >
                ).map((s) => {
                  const key =
                    typeof s === 'string'
                      ? s
                      : s?.id || s?.name || Math.random().toString(36);
                  const label = typeof s === 'string' ? s : s?.name || '-';
                  return (
                    <li key={key} className="badge badge-light capitalize">
                      {label.replace(/_/g, ' ')}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <div className={clsx('input input-bordered w-full')}>-</div>
          )}
        </div>
      </div>
    </div>
  );
};
